import React, { useEffect, useMemo, useRef, useState } from "react"
import Grid from "@mui/material/Grid"
import InputText from "../inputText/inputText"
import styles from "../../asset/styles/quotation/pricingSummary.module.scss"
import { agent } from "../../apiClient/api"
import { useSnackbar } from "../../context/snackbarContext"
import { useQuotation, createEmptyPricingTotals } from "../../context/quotationContext"
import { CategoryBreakdown, PricingMarkupItem, QuotationPricingSummaryPutBody } from "../../models/axiosModel"
import { toTitleCase } from "util/textcase"
import { getQuotationStatusIdFromQuotationStatusName, QuotationStatusVisualVariant } from "./quotationStatusVariant";

export type PricingSummarySaveIntent = QuotationStatusVisualVariant

interface PricingSummaryProps {
  onBindSaveHandler?: (handler: (intent?: PricingSummarySaveIntent) => Promise<boolean>) => void
}

const TONE_BY_INDEX = ["blue", "pink", "amber", "green"] as const

const formatUsd = (value: number): string =>
  `$${(Number.isFinite(value) ? value : 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const PricingSummary: React.FC<PricingSummaryProps> = ({ onBindSaveHandler }) => {
  const { showError, showSuccess } = useSnackbar()
  const {
    quotationId,
    quotationName,
    pricingCategoryBreakdown,
    setPricingCategoryBreakdown,
    pricingMarkups,
    setPricingMarkups,
    pricingTotals,
    setPricingTotals,
    discountUsd,
    setDiscountUsd,
  } = useQuotation()

  const [hasHydrated, setHasHydrated] = useState(false)
  // Local input strings keyed by category id — holds the per-category markup AMOUNT (USD).
  // Kept as strings so partial inputs like "12." don't lose the trailing dot while typing.
  const [markupAmountInputs, setMarkupAmountInputs] = useState<Record<number, string>>({})
  const [discountInput, setDiscountInput] = useState<string>(discountUsd || "0")
  const fetchedRef = useRef<string | null>(null)

  // ---- Fetch on mount (or when quotationId resolves) ----
  useEffect(() => {
    if (!quotationId) {
      setHasHydrated(true)
      return
    }
    if (fetchedRef.current === quotationId) return
    fetchedRef.current = quotationId

    let cancelled = false
    const hydrate = async () => {
      try {
        const response = await agent.QuotationPricingSummaryAPI.get(quotationId)
        if (cancelled) return
        const data = response?.status ? response.data : undefined
        const breakdown = (data?.category_breakdown || []) as CategoryBreakdown[]

        setPricingCategoryBreakdown(breakdown)

        // Seed markups state from breakdown (each row carries its own markup_amount).
        // Fall back to markup_amount_usd for older responses that used that key.
        const readAmount = (b: CategoryBreakdown): number =>
          Number((b as any).markup_amount ?? b.markup_amount_usd ?? 0)
        const seededMarkups: PricingMarkupItem[] = breakdown.map((b) => ({
          quotation_pricing_category_type_id: b.quotation_pricing_category_type_id,
          markup_amount: readAmount(b),
        }))
        setPricingMarkups(seededMarkups)

        const seededInputs: Record<number, string> = {}
        breakdown.forEach((b) => {
          if (b.quotation_pricing_category_type_id != null) {
            seededInputs[b.quotation_pricing_category_type_id] = String(readAmount(b))
          }
        })
        setMarkupAmountInputs(seededInputs)

        const dUsd = Number(data?.discount_usd || 0)
        setDiscountUsd(String(dUsd))
        setDiscountInput(String(dUsd))

        setPricingTotals({
          subtotal_usd: Number(data?.subtotal_usd || 0),
          total_markup_usd: Number(data?.total_markup_usd || 0),
          final_total_usd: Number(data?.final_total_usd || 0),
        })
      } catch {
        if (!cancelled) {
          showError("Failed to load pricing summary")
          setPricingCategoryBreakdown([])
          setPricingMarkups([])
          setPricingTotals(createEmptyPricingTotals())
        }
      } finally {
        if (!cancelled) setHasHydrated(true)
      }
    }
    hydrate()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotationId])

  // ---- Derived per-row values using current markup-amount inputs ----
  const rows = useMemo(() => {
    return (pricingCategoryBreakdown || []).map((b, idx) => {
      const id = Number(b.quotation_pricing_category_type_id ?? idx)
      const subtotal = Number(b.subtotal_usd ?? (Number(b.hotel_cost_usd || 0) + Number(b.land_cost_usd || 0)))
      const fallbackAmount = Number((b as any).markup_amount ?? b.markup_amount_usd ?? 0)
      const markupAmountRaw = markupAmountInputs[id] ?? String(fallbackAmount)
      const markupAmount = Number(markupAmountRaw) || 0
      return {
        id,
        label: b.category_name || `Category ${id}`,
        pax: Number(b.number_of_pax || 0),
        hotelCost: Number(b.hotel_cost_usd || 0),
        landCost: Number(b.land_cost_usd || 0),
        subtotal,
        markupAmountInput: markupAmountRaw,
        markupAmount,
        total: b.total_usd,
        tone: TONE_BY_INDEX[idx % TONE_BY_INDEX.length],
      }
    })
  }, [pricingCategoryBreakdown, markupAmountInputs])

  const summary = useMemo(() => {
    const subtotal = rows.reduce((sum, r) => sum + r.subtotal, 0)
    const totalMarkup = rows.reduce((sum, r) => sum + r.markupAmount, 0)
    const discountAmount = Number(discountInput) || 0
    const total = Math.max(0, subtotal + totalMarkup - discountAmount)
    return { subtotal, totalMarkup, total }
  }, [rows, discountInput])

  // Keep live totals in context so header/save flows can react immediately to user edits.
  useEffect(() => {
    let updatedSubTotal = 0;
    let updatedTotalMarkup = 0;
    let updatedFinalTotal = 0;

    pricingCategoryBreakdown.map((value) => {
      updatedSubTotal += (Number(Number(value.subtotal_usd).toFixed()) * Number(value.number_of_pax));
      updatedTotalMarkup += (Number(Number(value.markup_amount_usd).toFixed()) * Number(value.number_of_pax));
      return {};
    })
    updatedFinalTotal += (updatedTotalMarkup + updatedSubTotal) - Number(discountInput);
    setPricingTotals({
      subtotal_usd: updatedSubTotal,
      total_markup_usd: updatedTotalMarkup,
      final_total_usd: updatedFinalTotal,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingCategoryBreakdown, discountInput])

  const handleMarkupAmountChange = (id: number, raw: string) => {
    if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return
    setMarkupAmountInputs((prev) => ({ ...prev, [id]: raw }))
    const numeric = Number(raw) || 0
    setPricingMarkups((prev) => {
      const next = [...prev]
      const idx = next.findIndex((m) => Number(m.quotation_pricing_category_type_id) === id)
      if (idx === -1) {
        next.push({ quotation_pricing_category_type_id: id, markup_amount: numeric })
      } else {
        next[idx] = { ...next[idx], markup_amount: numeric }
      }
      return next
    })
    setPricingCategoryBreakdown((prev) => {
      return prev.map((value) => {
        if (typeof (value.quotation_pricing_category_type_id) === 'number' && value.quotation_pricing_category_type_id === id) {
          return {
            ...value,
            total_usd: Number(value?.subtotal_usd) + (numeric === 0 ? 1 : numeric),
            markup_amount_usd: numeric
          }
        }
        return value;
      })
    })
  }

  const handleDiscountChange = (raw: string) => {
    if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return
    setDiscountInput(raw)
    setDiscountUsd(raw)
  }

  // ---- PUT #12 — save pricing summary ----
  const savePricingSummary = React.useCallback(
    async (intent?: PricingSummarySaveIntent): Promise<boolean> => {
      if (!quotationId) {
        showError("Save Step 1 first to obtain a quotation id.")
        return false
      }
      const payload: QuotationPricingSummaryPutBody = {
        quotation_id: quotationId,
        quotation_name: quotationName,
        quotation_status_id: getQuotationStatusIdFromQuotationStatusName(intent ?? "Draft"),
        discount_usd: Number(discountInput) || 0,
        markup: pricingMarkups.map((m) => ({
          quotation_pricing_category_type_id: m.quotation_pricing_category_type_id,
          markup_amount: Number((m as any).markup_amount) || 0,
        })),
      }
      try {
        const response = await agent.QuotationPricingSummaryAPI.put(payload)
        if (!response?.status) {
          showError("Failed to save pricing summary")
          return false
        }
        showSuccess(intent === "ready to send" ? "Quotation finalized" : "Pricing summary saved")
        // Refresh totals from server response (backend may recompute markup/total).
        const data = response?.data
        if (data) {
          setPricingTotals({
            subtotal_usd: Number(data.subtotal_usd ?? summary.subtotal),
            total_markup_usd: Number(data.total_markup_usd ?? summary.totalMarkup),
            final_total_usd: Number(data.final_total_usd ?? summary.total),
          })
          if (data.discount_usd != null) {
            const nextDiscount = String(Number(data.discount_usd || 0))
            setDiscountUsd(nextDiscount)
            setDiscountInput(nextDiscount)
          }
        }
        return true
      } catch {
        showError("Failed to save pricing summary")
        return false
      }
    },
    [quotationId, quotationName, discountInput, pricingMarkups, summary.subtotal, summary.totalMarkup, summary.total, showError, showSuccess, setPricingTotals, setDiscountUsd],
  )

  const saveHandlerRef = useRef(savePricingSummary)
  saveHandlerRef.current = savePricingSummary
  useEffect(() => {
    if (!onBindSaveHandler) return
    onBindSaveHandler((intent) => saveHandlerRef.current(intent))
  }, [onBindSaveHandler])

  // Touch unused-but-context-bound state to silence linter (markups already covered above).
  void pricingTotals

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 12 }}>
        <div className={styles.contentCard}>
          <h2 className={styles.sectionTitle}>Pricing Summary</h2>

          {!hasHydrated && <p style={{ color: "#8c97aa", fontSize: 12, fontStyle: "italic" }}>Loading pricing summary…</p>}

          {hasHydrated && rows.length === 0 && (
            <p style={{ color: "#8c97aa", fontSize: 12, fontStyle: "italic" }}>
              No pricing categories returned. Save Step 2 first.
            </p>
          )}

          {hasHydrated && rows.length > 0 && (
            <>
              <div className={styles.tableWrap}>
                <div className={styles.tableHead}>
                  <div>Category</div>
                  <div>No. Of Pax</div>
                  <div>Hotel Cost (USD)</div>
                  <div>Land Cost (USD)</div>
                  <div>Subtotal (USD)</div>
                  <div>Markup Amount (USD)</div>
                  <div>Total (USD)</div>
                </div>

                {rows.map((row) => (
                  <div key={row.id} className={`${styles.tableRow} ${styles[row.tone]}`}>
                    <div>{toTitleCase(row.label)}</div>
                    <div>{row.pax}</div>
                    <div>{formatUsd(row.hotelCost)}</div>
                    <div>{formatUsd(row.landCost)}</div>
                    <div className={styles.strongValue}>{formatUsd(row.subtotal)}</div>
                    <div>
                      <InputText
                        label=""
                        hideLabel={true}
                        noMargin={true}
                        value={row.markupAmountInput}
                        onChange={(event) => handleMarkupAmountChange(row.id, event.target.value)}
                        height="30px"
                        width="110px"
                      />
                    </div>
                    <div className={styles.strongValue}>{formatUsd(row.total ?? 0)}</div>
                  </div>
                ))}
              </div>

              <div className={styles.totalsCard}>
                <div className={styles.totalLine}>
                  <span>Subtotal</span>
                  <strong>{formatUsd(pricingTotals.subtotal_usd)}</strong>
                </div>
                <div className={styles.totalLine}>
                  <span>Total Markup</span>
                  <strong className={styles.positiveValue}>{formatUsd(pricingTotals.total_markup_usd)}</strong>
                </div>
                <div className={styles.totalLine}>
                  <span>Discount (USD)</span>
                  <InputText
                    label=""
                    hideLabel={true}
                    noMargin={true}
                    value={discountInput}
                    onChange={(event) => handleDiscountChange(event.target.value)}
                    height="34px"
                    width="100px"
                  />
                </div>
                <div className={`${styles.totalLine} ${styles.grandTotal}`}>
                  <span>Total</span>
                  <strong>{formatUsd(pricingTotals.final_total_usd)}</strong>
                </div>
              </div>
            </>
          )}
        </div>
      </Grid>
    </Grid>
  )
}

export default PricingSummary
